import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, Box } from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import DraftsIcon from '@mui/icons-material/Drafts';
import ChatIcon from '@mui/icons-material/Chat';
import { useAuthContext } from '../../context/AuthContext';
import NavigationGuardModal from '../common/NavigationGuardModal';

// Custom event for navigation intent
export const NAVIGATION_INTENT_EVENT = 'navigationIntent';
export interface NavigationIntentDetail {
  targetPath: string;
  callback: (shouldProceed: boolean) => void;
}

const Navbar: React.FC = () => {
  const { isAuthenticated, logout } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{
    path: string;
    isLogout?: boolean;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isInChatRoute = location.pathname.startsWith('/chat');

  const handleNavigation = (path: string, isLogout?: boolean) => {
    // Don't show confirmation if already on the target path
    if (path === location.pathname) return;

    // Only use navigation guard in chat route
    if (isInChatRoute) {
      // Create a custom event to check for unsaved changes
      const event = new CustomEvent<NavigationIntentDetail>(
        NAVIGATION_INTENT_EVENT,
        {
          detail: {
            targetPath: path,
            callback: (shouldProceed: boolean) => {
              if (shouldProceed) {
                // No unsaved changes, proceed with navigation
                if (isLogout) {
                  logout();
                }
                navigate(path);
              } else {
                // Has unsaved changes, show confirmation modal
                setPendingNavigation({ path, isLogout });
                setShowNavigationModal(true);
              }
            },
          },
        }
      );

      // Dispatch the event to be caught by ChatSection
      window.dispatchEvent(event);
    } else {
      // Direct navigation outside chat route
      if (isLogout) {
        logout();
      }
      navigate(path);
    }
  };

  useEffect(() => {
    const handleSavingState = (event: CustomEvent<{ saving: boolean }>) => {
      setIsSaving(event.detail.saving);
    };

    const handleCloseModal = () => {
      setShowNavigationModal(false);
      setPendingNavigation(null);
    };

    // Only listen for events in chat route
    if (isInChatRoute) {
      window.addEventListener(
        'savingStateChange',
        handleSavingState as EventListener
      );
      window.addEventListener(
        'closeNavigationModal',
        handleCloseModal as EventListener
      );
    }

    return () => {
      window.removeEventListener(
        'savingStateChange',
        handleSavingState as EventListener
      );
      window.removeEventListener(
        'closeNavigationModal',
        handleCloseModal as EventListener
      );
    };
  }, [isInChatRoute]);

  const handleModalClose = () => {
    setShowNavigationModal(false);
    setPendingNavigation(null);
  };

  const handleDiscard = () => {
    if (pendingNavigation) {
      if (pendingNavigation.isLogout) {
        logout();
      }
      navigate(pendingNavigation.path);
    }
    handleModalClose();
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            KI Agent
          </Typography>
          {isAuthenticated && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                color="inherit"
                onClick={() => handleNavigation('/drafts')}
                startIcon={<DraftsIcon />}
              >
                Drafts
              </Button>
              <Button
                color="inherit"
                onClick={() => handleNavigation('/agents')}
                startIcon={<PeopleIcon />}
              >
                AI Agents
              </Button>
              <Button
                color="inherit"
                onClick={() => handleNavigation('/', true)}
                startIcon={<LogoutIcon />}
              >
                Logout
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Only render modal in chat route */}
      {isInChatRoute && (
        <NavigationGuardModal
          open={showNavigationModal}
          onClose={handleModalClose}
          onSave={() => {
            // This will be handled by ChatSection component
            const event = new CustomEvent('saveAndNavigate', {
              detail: {
                targetPath: pendingNavigation?.path,
                isLogout: pendingNavigation?.isLogout,
              },
            });
            window.dispatchEvent(event);
          }}
          onDiscard={handleDiscard}
          isSaving={isSaving}
        />
      )}
    </>
  );
};

export default Navbar;
